/**
 * Component Manager - component registration and rendering delegation
 */
namespace appn.designerv2.componentManager {
    
    let _sectors: IHeading[];
    let _definitions = new Map<string, IComponentDefinition>();

    export function registerSectors(sectors: IHeading[]) {
        _sectors = sectors;
    }

    export function getSectors(): IHeading[] {
        return _sectors;
    }
    
    export function registerDefinitions<T extends IComponentDefinition>(defs: { [type: string]: T }) {
        Object.entries(defs).forEach(([type, def]) => registerDefinition(type, def));
    }
    
    export function registerDefinition<T extends IComponentDefinition>(type: string, definition: T): void {
        _definitions.set(type, definition);
    }
    
    export function getDefinitions(): IComponentDefinition[] {
        return Array.from(_definitions.values());
    }

    export function isRegistered(componentType: string): boolean {
        return _definitions.has(componentType);
    }
    
    export function getByCategory(category: string): IComponentDefinition[] {
        return Array.from(_definitions.values()).filter(def => def.category === category);
    }
    
    export function getDefinition(type: string): IComponentDefinition | undefined {
        return _definitions.get(type);
    }

    export function getDefaultProperties(definition: IComponentDefinition): any {
        const properties: any = {};
        Object.entries(definition.properties).forEach(([key, propDef]) => {
            if (propDef.defaultValue !== undefined) {
                const propKey = propDef.path || key;
                if (propDef.path) {
                    setPropertyByPath(properties, propDef.path, appn.utils.deepClone(propDef.defaultValue));
                } else {
                    properties[propKey] = appn.utils.deepClone(propDef.defaultValue);
                }
            }
        });
        return properties;
    }

    export function createComponent(type: string): IComponent | null {
        const def = _definitions.get(type);
        if (!def)
            return null;

        const newComponent: IComponent = {
            id: appn.utils.newGuid(true),
            type: type,
            gridPosition: { x: 0, y: 0, w: 4, h: 4 },
        };

        const dataQueryPropNames = getDataQueryPropertyNames(def);
        for (const dataQueryPropName of dataQueryPropNames) {
            if (!newComponent[dataQueryPropName]) {
                newComponent[dataQueryPropName] = {
                    name: 'default',
                    primaryDataset: { alias: 'primary' }
                };
            }
        }

        // Set default values
        for (const propName in def.properties) {
            const propDef = def.properties[propName];
            if (propDef.defaultValue !== undefined) {
                if (propDef.path)
                    appn.designerv2.setPropertyByPath(newComponent, propDef.path, JSON.parse(JSON.stringify(propDef.defaultValue)));
                else
                    (newComponent as any)[propName] = JSON.parse(JSON.stringify(propDef.defaultValue));
            }
        }

        return newComponent as IComponent;
    }
    
    export function getDataQueryPropertyNames(definition: IComponentDefinition): string[] {
        if (!definition)
            return [];

        const dataQueryNames = new Set<string>();
        for (const propName in definition.properties) {
            const propDef = definition.properties[propName];
            if (propDef.type === 'datasetSelector' && propDef.path) {
                const rootPropName = propDef.path.split('.')[0];
                dataQueryNames.add(rootPropName);
            }
        }

        return Array.from(dataQueryNames);
    }
}
